import type { SchemaField } from './schema';


// A blank new row, seeded from the schema's own declared defaults — never
// hand-written field values, same "schema is the source of truth" rule as
// everything else on this page.
export function blankItemFromFields(fields: SchemaField[]): Record<string, unknown> {
  const obj: Record<string, unknown> = {};
  for (const f of fields) {
    obj[f.path] = f.default ?? (f.type === "bool" ? false : f.type === "int" || f.type === "float" ? 0 : "");
  }
  return obj;
}


export function newRowKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `row-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}


// Record title/subtitle, derived generically from whatever keys an item
// happens to carry — never hardcoded per section, since the same rule has
// to work identically for regions, brokers, hazard sources, etc.
//
// 2026-09-27 first-installer review (part5d, item 5): a brand-new,
// not-yet-named record used to fall all the way through to a numbered
// "Item 1" — meaningless, and NOT the same thing as the record's own
// instance id: a dynamic-keyed section's new-instance id (e.g. a new
// transport's own operator-typed key) was already sitting right there in
// the caller's own state and simply wasn't being looked at. `fallback` is
// now supplied by the caller instead of a bare index — either that real
// typed id (DynamicKeyedSectionEditor) or a generic "New <noun>" (list
// sections, whose row key is a synthetic, meaningless UUID/index, not
// something a person typed).
export function recordTitle(item: Record<string, unknown>, fallback: string): string {
  const label = item.label;
  if (typeof label === "string" && label) return label;
  const name = item.name;
  if (typeof name === "string" && name) return name;
  const id = item.id;
  if (typeof id === "string" && id) return id;
  return fallback;
}


export function recordSubtitle(item: Record<string, unknown>, title: string): string | null {
  const id = item.id;
  if (typeof id === "string" && id && id !== title) return id;
  const type = item.type;
  if (typeof type === "string" && type) return type;
  const family = item.family;
  if (typeof family === "string" && family) return family;
  return null;
}


// "+ Add <noun>" — a singular noun mechanically derived from the section's
// own last path segment ("mqtt.brokers" -> "broker", "regions" -> "region",
// "hazards.region_routes" -> "region route"), never a hand-maintained
// per-section string.
export function recordNounSingular(path: string): string {
  const idx = path.lastIndexOf(".");
  const seg = idx === -1 ? path : path.slice(idx + 1);
  const words = seg.replace(/_/g, " ");
  return words.endsWith("s") ? words.slice(0, -1) : words;
}


// Save target for a list section — see this file's header comment for the
// full reasoning. Generic on however many segments follow the top-level
// one (today it's always exactly one), rather than a hardcoded six-way
// switch.
export function listSectionSaveTarget(path: string, items: Record<string, unknown>[]): { url: string; body: Record<string, unknown> } {
  const segments = path.split(".");
  const section = segments[0];
  const rest = segments.slice(1);
  if (rest.length === 0) {
    return { url: `/api/config/${encodeURIComponent(section)}`, body: { patch: items } };
  }
  let nested: unknown = items;
  for (let i = rest.length - 1; i >= 0; i--) {
    nested = { [rest[i]]: nested };
  }
  return { url: `/api/config/${encodeURIComponent(section)}`, body: { patch: nested as Record<string, unknown> } };
}
